import { ZodType, z } from 'zod';
import { validatePhone } from '../../../../../shared/utils';
import { Status } from '../../../../enums/contractor';
import { ExperienceType } from '../../../../enums/experience';
import { DEFAULT_TEMPLATE } from '../FormSections/ReservationRequestSections/RsvpConfig';
import {
    jobHasSelectedContractor,
    maxSeatingFromCommercialSpaces,
    positiveGuestCap,
} from '../../EditReservation/reservationEdit.utils';
import { ProviderType } from '../../../../../app/Slices/SevenDayApiTypes';

const vehicleInfo = (experienceDetails: any) =>
    z.object({
        checkinTime: z.string(),
        checkoutTime: z.string(),
        driverSameAsBooker: z.boolean().default(false),
        drivers: z.array(
            z.object({
                firstName: z.string().min(1, {
                    message: 'Required',
                }),
                lastName: z.string().min(1, {
                    message: 'Required',
                }),
                phone: z.string().refine(validatePhone, {
                    message: 'Invalid phone number',
                }),
                email: z.string().email(),
                licenceNo: z.string().min(1, {
                    message: 'Required',
                }),
                licenceType: z
                    .enum(['full', 'p1', 'p2'], { message: 'Required' })
                    .superRefine((val, ctx) => {
                        if (
                            (experienceDetails?.details
                                ?.minimumLicenceRequired === 'full' &&
                                (val === 'p1' || val === 'p2')) ||
                            (experienceDetails?.details
                                ?.minimumLicenceRequired === 'p2' &&
                                val === 'p1')
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `The vehicle requires a ${experienceDetails?.details?.minimumLicenceRequired} license as the minimum qualification for drivers. `,
                            });
                        }
                    }),
            }),
        ),
    });

const commercialInfo = (experienceDetails: any) =>
    z.object({
        checkinTime: z.string(),
        checkoutTime: z.string(),
        spaceType: z.string(),
        reqMeetingRoom: z.boolean().default(false),
        requiresCatering: z.boolean().default(false),
        cateringNotes: z.string().default(''),
        guests: z.number(),
    });

const boatInfo = (experienceDetails: any) =>
    z.object({
        // moduleType: experienceDetails?.type,
        checkinTime: z.string(),
        checkoutTime: z.string(),
        adults: z.number().min(1, {
            message: 'At least 1 guest required',
        }),
        children: z.number(),
        overnightGuests: z
            .number()
            .max(experienceDetails?.details?.overnightGuests ?? Infinity, {
                message: `Maximum ${experienceDetails?.details?.overnightGuests} overnight guests allowed`,
            }),
        requiresCatering: z.boolean().default(false),
        cateringNotes: z.string().default(''),
        requiresBeverages: z.boolean().default(false),
        beverageNotes: z.string().default(''),
    });

const activityInfo = (experienceDetails: any) =>
    z.object({
        checkinTime: z.string(),
        checkoutTime: z.string(),
        guests: z.number().min(1, {
            message: 'At least 1 guest required',
        }),
    });

const propertyInfo = (experienceDetails: any) =>
    z.object({
        // moduleType: experienceDetails?.type,
        checkinTime: z.string(),
        checkoutTime: z.string(),
        adults: z.number().min(1, {
            message: 'At least 1 guest required',
        }),
        children: z.number(),
        overnightGuests:
            experienceDetails?.details?.overnightGuests === 0
                ? z.number().default(0).optional()
                : z
                      .number()
                      .min(1, {
                          message: 'At least 1 overnight guest required.',
                      })
                      .max(
                          experienceDetails?.details?.overnightGuests ??
                              Infinity,
                          {
                              message: `Maximum ${experienceDetails?.details?.overnightGuests} overnight guests allowed`,
                          },
                      )
                      .default(0)
                      .optional(),
        requiresCatering: z.boolean().default(false),
        cateringNotes: z.string().default(''),
        cateringContractor: z.number().optional(),
        requiresBeverages: z.boolean().default(false),
        beverageNotes: z.string().default(''),
        requiresMassages: z.boolean().default(false),
        massageNotes: z.string().default(''),
    });

export const bookingInfo = (experienceDetails: any) =>
    z
        .object({
            visitType: z.enum(['personal', 'business']),
            notes: z.string().default(''),
        })
        .merge(
            experienceDetails?.type === 'commercial'
                ? commercialInfo(experienceDetails)
                : experienceDetails?.type === 'vehicle'
                  ? vehicleInfo(experienceDetails)
                  : experienceDetails?.type === 'boat'
                    ? boatInfo(experienceDetails)
                    : experienceDetails?.type === 'activity'
                      ? activityInfo(experienceDetails)
                      : propertyInfo(experienceDetails),
        )
        .superRefine((val, ctx) => {
            if (
                experienceDetails?.type !== ExperienceType.Commercial ||
                !experienceDetails?.details
            ) {
                return;
            }
            const details = experienceDetails.details;
            const commercialVal = val as unknown as {
                spaceType: string;
                reqMeetingRoom: boolean;
                guests: number;
            };

            if (
                (commercialVal.spaceType === 'meetingRoom' ||
                    commercialVal.spaceType === 'entireVenue' ||
                    commercialVal.reqMeetingRoom) &&
                commercialVal.guests === 0
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `At least 1 guest required`,
                    path: ['guests'],
                });
            }
        });

const jobItemSchema = z.object({
    template: z.object({
        id: z.number(),
        name: z.string(),
    }),
    pricingItem: z
        .object({
            id: z.number(),
        })
        .optional(),
    cost: z.number(),
    description: z.string().optional().nullable(),
    name: z.string(),
    quantity: z.number().min(1, { message: 'Quantity must be at least 1' }),
    total: z.number(),
});

export type ReservationRequestSchemaEditOptions = {
    /**
     * Approved booking currently in its date range: edit UI only allows attendee lists.
     * Skip strict job / optional-services / contractor-policy validation so save is not blocked.
     */
    attendeesOnlyActiveBooking?: boolean;
};

function createJobSchema(attendeesOnlyRelaxed: boolean) {
    return z
        .object({
            contractor: z
                .object({
                    // New rows use '' until a contractor is selected; API rows use numeric id
                    id: z.union([
                        z.number(),
                        z.string().min(1, { message: 'Required' }),
                    ]),
                })
                .passthrough(),
            setMenu: z
                .object({
                    id: z.number(),
                    name: z.string(),
                })
                .optional()
                .nullable(),
            quantity: z.number().optional(),
            jobDate: z.string().min(1, { message: 'Required' }),
            startTime: z.string().nullable(),
            endTime: z.string().nullable(),
            ownerNotes: z.string().default(''),
            guestNotes: z.string().default(''),
            items: z.array(jobItemSchema),
            services: z.array(z.any()).default([]),
            templateId: z.coerce.number().optional(),
            createdBy: z.enum(['guest', 'owner']).optional(),
            contractorHasTemplates: z.boolean().optional(),
            providerType: z.nativeEnum(ProviderType),
            //validation happens in the contractor policies component
            policy: z
                .object({
                    checked: z.boolean(),
                })
                .nullable()
                .optional(),
            /** Client-only: must survive Zod parse so edit flow can POST new contractor jobs */
            newJob: z.boolean().optional(),
            id: z.union([z.number(), z.string()]).optional(),
            status: z.string().optional(),
            serviceType: z.string().optional(),
        })
        .passthrough()
        .superRefine((val, ctx) => {
            if (attendeesOnlyRelaxed) {
                return;
            }

            if (val.providerType === ProviderType.Contractor) {
                if (val.createdBy === undefined) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['createdBy'],
                    });
                }
                if (val.policy && !val.policy?.checked) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Please accept the contractor's policy",
                        path: ['policy', 'checked'],
                    });
                }
            }

            if (
                val.createdBy === 'guest' &&
                val?.items?.length === 0 &&
                val?.providerType === ProviderType.Contractor
            ) {
                if (val.contractorHasTemplates) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Select at least one job item to continue',
                        path: ['primaryActionField'],
                    });
                } else if (val.guestNotes === '') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Add notes to your job request to continue',
                        path: ['guestNotes'],
                    });
                }
            }

            const skipTimeChecks =
                val.status === Status.Cancelled || val.createdBy === 'owner';

            if (val.providerType === ProviderType.Owner && !val?.setMenu?.id) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message:
                        'Please select one of the available set menus for your request',
                    path: ['primaryActionField'],
                });
            }

            if (!skipTimeChecks) {
                if (!jobHasSelectedContractor(val)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Please select a contractor',
                        path: ['contractor', 'id'],
                    });
                }
                if (!val.startTime) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['startTime'],
                    });
                }
                if (!val.endTime) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Required',
                        path: ['endTime'],
                    });
                }
            }
        });
}

/** Default strict schema (new requests, full edit). */
export const jobSchema = createJobSchema(false);

function createJobsSchema(attendeesOnlyRelaxed: boolean) {
    return z.record(createJobSchema(attendeesOnlyRelaxed)).default({});
}

function hasAdults(bookingInfo: any): bookingInfo is { adults: number } {
    return 'adults' in bookingInfo;
}

function hasChildren(bookingInfo: any): bookingInfo is { children: number } {
    return 'children' in bookingInfo;
}

/** Commercial guest cap: max(module meetingRoomMaxGuests / maxGuests, selected boardroom seatingCapacity). */
function refineCommercialGuestLimitsAtBookingInfo(
    bookingInfo: {
        spaceType: string;
        reqMeetingRoom: boolean;
        guests: number;
    },
    spaces: unknown,
    experienceDetails: any,
    ctx: z.RefinementCtx,
    attendeesOnlyActiveBooking?: boolean,
) {
    if (attendeesOnlyActiveBooking) {
        return;
    }
    if (
        experienceDetails?.type !== ExperienceType.Commercial ||
        !experienceDetails?.details
    ) {
        return;
    }
    const details = experienceDetails.details;
    const guests = Number(bookingInfo.guests ?? 0);

    if (bookingInfo.spaceType === 'meetingRoom' || bookingInfo.reqMeetingRoom) {
        const moduleCap = positiveGuestCap(details.meetingRoomMaxGuests);
        const fromSpaces = maxSeatingFromCommercialSpaces(spaces);
        const combined = Math.max(moduleCap ?? 0, fromSpaces);
        const cap = combined > 0 ? combined : null;
        if (cap != null && guests > cap) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Maximum ${cap} guests allowed`,
                path: ['bookingInfo', 'guests'],
            });
        }
        return;
    }

    if (bookingInfo.spaceType === 'entireVenue') {
        const cap = positiveGuestCap(details.maxGuests);
        if (cap != null && guests > cap) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Maximum ${cap} guests allowed`,
                path: ['bookingInfo', 'guests'],
            });
        }
    }
}

/** Day-guest cap: activity `quantity`, property/boat `maxGuests` (adults + children). */
function refineDayGuestLimitsAtBookingInfo(
    bookingInfo: unknown,
    experienceDetails: any,
    ctx: z.RefinementCtx,
    attendeesOnlyActiveBooking?: boolean,
) {
    if (attendeesOnlyActiveBooking) {
        return;
    }
    const details = experienceDetails?.details;
    if (!details || !bookingInfo || typeof bookingInfo !== 'object') {
        return;
    }

    const type = experienceDetails?.type;

    if (type === ExperienceType.Activity || type === 'activity') {
        const cap = positiveGuestCap(details.quantity);
        const guests = Number((bookingInfo as { guests?: number }).guests ?? 0);
        if (cap != null && guests > cap) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Maximum ${cap} guests allowed`,
                path: ['bookingInfo', 'guests'],
            });
        }
        return;
    }

    if (
        (type === 'property' || type === 'boat') &&
        hasAdults(bookingInfo) &&
        hasChildren(bookingInfo)
    ) {
        const cap = positiveGuestCap(details.maxGuests);
        if (cap == null) {
            return;
        }
        const total =
            Number(bookingInfo.adults ?? 0) + Number(bookingInfo.children ?? 0);
        if (total > cap) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Maximum ${cap} guests allowed`,
                path: ['bookingInfo', 'children'],
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Maximum ${cap} guests allowed`,
                path: ['bookingInfo', 'adults'],
            });
        }
    }
}

function refineRsvpAttendeesWhenEnabled(
    val: {
        rsvpConfig?: { rsvpEnabled?: boolean };
        staffAttending?: { email: string }[];
        externalAttendees?: { email: string }[];
    },
    ctx: z.RefinementCtx,
    attendeesOnlyActiveBooking?: boolean,
) {
    if (attendeesOnlyActiveBooking) {
        return;
    }
    if (!val.rsvpConfig?.rsvpEnabled) {
        return;
    }

    const staffCount = val.staffAttending?.length ?? 0;
    const guestCount = val.externalAttendees?.length ?? 0;
    if (staffCount + guestCount > 0) {
        return;
    }

    const message =
        'Add at least one staff or guest attendee when RSVP is enabled.';

    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: ['staffAttending'],
    });
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '',
        path: ['externalAttendees'],
    });
}

function refineOptionalServicesWhenRequired(
    val: any,
    jobs: any,
    experienceDetails: any,
    ctx: z.RefinementCtx,
    attendeesOnlyActiveBooking?: boolean,
) {
    if (attendeesOnlyActiveBooking) return;
    if (!val?.requiresServices) return;
    if (Object.values(jobs).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please add at least one service request',
            path: ['requiresServices'],
        });
    }
}

function experienceUsesNotesBasedOptionalServices(
    experienceDetails: any,
): boolean {
    const hasContractors = (experienceDetails?.contractors?.length ?? 0) > 0;
    const hasSetMenus = (experienceDetails?.setMenus?.length ?? 0) > 0;

    return !hasContractors && !hasSetMenus;
}

function refineNotesBasedOptionalServices(
    bookingInfo: Record<string, unknown>,
    experienceDetails: any,
    ctx: z.RefinementCtx,
    attendeesOnlyActiveBooking?: boolean,
) {
    if (attendeesOnlyActiveBooking) return;
    if (!experienceUsesNotesBasedOptionalServices(experienceDetails)) return;

    if (
        bookingInfo.requiresCatering === true &&
        'cateringNotes' in bookingInfo &&
        !(bookingInfo.cateringNotes as string | undefined)?.trim()
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Add notes to your catering request',
            path: ['bookingInfo', 'cateringNotes'],
        });
    }

    if (
        bookingInfo.requiresBeverages === true &&
        'beverageNotes' in bookingInfo &&
        !(bookingInfo.beverageNotes as string | undefined)?.trim()
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Add notes to your beverage request',
            path: ['bookingInfo', 'beverageNotes'],
        });
    }

    if (
        bookingInfo.requiresMassages === true &&
        'massageNotes' in bookingInfo &&
        !(bookingInfo.massageNotes as string | undefined)?.trim()
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Add notes to your massage request',
            path: ['bookingInfo', 'massageNotes'],
        });
    }
}

export const waitListRequestSchema = (experienceDetails: any) =>
    z.object({
        bookingInfo: z
            .object({
                visitType: z.enum(['personal', 'business']),
                notes: z.string().default(''),
            })
            .merge(
                experienceDetails?.type === 'commercial'
                    ? z.object({
                          spaceType: z.string(),
                          reqMeetingRoom: z.boolean().default(false),
                      })
                    : z.object({
                          checkinTime: z.string(),
                          checkoutTime: z.string(),
                      }),
            ),
    });

export const rsvpConfigSchema = z
    .object({
        rsvpEnabled: z.boolean(),
        rsvpMessage: z.string(),
        autoRemindersEnabled: z.boolean(),
        reminderDaysBeforeEvent: z.coerce.number(),
    })
    .superRefine((val, ctx) => {
        if (val.rsvpEnabled && !val.rsvpMessage.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['rsvpMessage'],
            });
        }
        if (val.autoRemindersEnabled && !val.reminderDaysBeforeEvent) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Number must be greater than or equal to 1',
                path: ['reminderDaysBeforeEvent'],
            });
        }
    });

const reservationSpaceEntrySchema = z
    .object({
        id: z.number(),
        startDate: z.string(),
        endDate: z.string(),
        checkinTime: z.string().optional(),
        checkoutTime: z.string().optional(),
        type: z
            .enum([
                'privateOffice',
                'boardroom',
                'bedroom',
                'bathroom',
                'livingRoom',
                'diningArea',
                'kitchen',
                'gameRoom',
                'garage',
            ])
            .optional(),
        attendees: z
            .array(
                z
                    .object({
                        email: z.string().email().optional(),
                        name: z.string().trim().min(1).optional(),
                    })
                    .refine((value) => !!value.email || !!value.name, {
                        message:
                            'Attendee must have an email or a placeholder name.',
                    }),
            )
            .default([]),
    })
    .passthrough();

const attendeesSchema = z
    .array(
        z.object({
            email: z.string().email(),
        }),
    )
    .default([]);

export const GuestRequestSchema = (
    experienceDetails: any,
    editOptions?: ReservationRequestSchemaEditOptions,
) => {
    const relaxed = editOptions?.attendeesOnlyActiveBooking === true;

    const guestPolicyObject = z.object({
        checked: z.boolean().default(false),
        requiresConsent: z.boolean(),
        description: z.string(),
        name: z.string(),
        cancellationType: z.string(),
        policyId: z.number(),
    });

    const guestBookingPolicyField = relaxed
        ? guestPolicyObject.optional()
        : guestPolicyObject
              .superRefine((val, ctx) => {
                  if (val.requiresConsent && !val.checked) {
                      ctx.addIssue({
                          code: z.ZodIssueCode.custom,
                          message: 'Please accept this policy',
                          path: ['checked'],
                      });
                  }
              })
              .optional();

    return z
        .object({
            booker: z.object({
                id: z.number(),
            }),
            aiConversationId: z.string().optional().nullable(),
            waitListId: z.number().optional().nullable(),
            mainBooking: z
                .object({
                    id: z.number(),
                })
                .optional()
                .nullable(),
            isAddOn: z.boolean().default(false),
            bookingInfo: bookingInfo(experienceDetails),
            policy: guestBookingPolicyField,
            staffAttending: attendeesSchema,
            externalAttendees: attendeesSchema,
            rsvpConfig: rsvpConfigSchema,
            spaces: z.array(reservationSpaceEntrySchema),
            jobs: createJobsSchema(relaxed),
            requiresServices: z.boolean().default(false),
        })
        .superRefine((val, ctx) => {
            if (experienceDetails?.type !== ExperienceType.Commercial) {
                return;
            }
            const commercialVal = val.bookingInfo as unknown as {
                spaceType: string;
                reqMeetingRoom: boolean;
                guests: number;
            };

            if (
                commercialVal.spaceType === 'entireVenue' ||
                commercialVal.spaceType === 'meetingRoom' ||
                commercialVal.reqMeetingRoom
            ) {
                if (
                    val.staffAttending.length + val.externalAttendees.length >
                    commercialVal.guests
                ) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: ``,
                        path: ['staffAttending'],
                    });
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Staff and guest attendees cannot exceed guest count`,
                        path: ['externalAttendees'],
                    });
                }
            }
        })
        .superRefine((val, ctx) => {
            refineOptionalServicesWhenRequired(
                val,
                val.jobs,
                experienceDetails,
                ctx,
                relaxed,
            );
        })
        .superRefine((val, ctx) => {
            refineCommercialGuestLimitsAtBookingInfo(
                val.bookingInfo as unknown as {
                    checkinTime: string;
                    checkoutTime: string;
                    spaceType: string;
                    reqMeetingRoom: boolean;
                    guests: number;
                },
                val.spaces,
                experienceDetails,
                ctx,
                relaxed,
            );
        })
        .superRefine((val, ctx) => {
            refineDayGuestLimitsAtBookingInfo(
                val.bookingInfo,
                experienceDetails,
                ctx,
                relaxed,
            );
        })
        .superRefine((val, ctx) => {
            refineRsvpAttendeesWhenEnabled(val, ctx, relaxed);
        })
        .superRefine((val, ctx) => {
            refineNotesBasedOptionalServices(
                val.bookingInfo,
                experienceDetails,
                ctx,
                relaxed,
            );
        });
};

export const OwnerRequestSchema = (
    experienceDetails: any,
    editOptions?: ReservationRequestSchemaEditOptions,
) => {
    const relaxed = editOptions?.attendeesOnlyActiveBooking === true;

    const ownerOnBehalfPolicyObject = z.object({
        checked: z.boolean().default(false),
        requiresConsent: z.boolean(),
    });

    const ownerOnBehalfPolicyField = relaxed
        ? ownerOnBehalfPolicyObject.optional()
        : ownerOnBehalfPolicyObject
              .superRefine((val, ctx) => {
                  if (val.requiresConsent && !val.checked) {
                      ctx.addIssue({
                          code: z.ZodIssueCode.custom,
                          message: 'Please accept this policy',
                          path: ['checked'],
                      });
                  }
              })
              .optional();

    const ownerContractorPoliciesField = relaxed
        ? z
              .array(
                  z.object({
                      checked: z.boolean().default(false),
                  }),
              )
              .default([])
        : z
              .array(
                  z.object({
                      checked: z
                          .boolean()
                          .default(false)
                          .refine((v) => v === true, {
                              message: 'Please accept this policy',
                          }),
                  }),
              )
              .default([]);

    return z
        .discriminatedUnion('onBehalf', [
            z.object({
                onBehalf: z.literal(true),
                bookingInfo: bookingInfo(experienceDetails),
                policy: ownerOnBehalfPolicyField,
                jobs: createJobsSchema(relaxed),
                requiresServices: z.boolean().default(false),
                contractorPolicies: ownerContractorPoliciesField,
                spaces: z.array(reservationSpaceEntrySchema),
                mainBooking: z
                    .object({
                        id: z.number(),
                    })
                    .optional()
                    .nullable(),
                isAddOn: z.boolean().default(false),
                staffAttending: attendeesSchema,
                removeNirovisionAccess: z.boolean().default(false),
            }),
            z.object({
                onBehalf: z.literal(false),
                reason: z.enum([
                    'cleaning',
                    'maintenance',
                    'publicHoliday',
                    'staffAway',
                    'shutdownPeriod',
                    'other',
                ]),
                notes: z.string().default(''),
                staffAttending: attendeesSchema,
                removeNirovisionAccess: z.boolean().default(false),
                requiresServices: z.boolean().default(false),
            }),
        ])
        .superRefine((val, ctx) => {
            if (!val.onBehalf && !val.reason) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['reason'],
                });
            }

            if (!val.onBehalf && val.reason === 'other' && !val.notes) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Required',
                    path: ['notes'],
                });
            }

            if (val.onBehalf) {
                refineOptionalServicesWhenRequired(
                    val.bookingInfo,
                    val.jobs,
                    experienceDetails,
                    ctx,
                    relaxed,
                );
                refineCommercialGuestLimitsAtBookingInfo(
                    val.bookingInfo as unknown as {
                        spaceType: string;
                        reqMeetingRoom: boolean;
                        guests: number;
                    },
                    val.spaces,
                    experienceDetails,
                    ctx,
                    relaxed,
                );
                refineDayGuestLimitsAtBookingInfo(
                    val.bookingInfo,
                    experienceDetails,
                    ctx,
                    relaxed,
                );
                refineNotesBasedOptionalServices(
                    val.bookingInfo,
                    experienceDetails,
                    ctx,
                    relaxed,
                );
            }
        });
};

export const getRequestSchema = (
    experienceDetails: any,
    userRole: string,
    editOptions?: ReservationRequestSchemaEditOptions,
): ZodType => {
    if (userRole.includes('guest')) {
        return GuestRequestSchema(experienceDetails, editOptions);
    }
    return OwnerRequestSchema(experienceDetails, editOptions);
};

export const getDefaultValues = (
    experienceDetails: any,
    overnight: boolean,
    user: any,
) => {
    let values = {};

    let bookingInfo: any = {
        visitType: experienceDetails?.visitType,
        notes: '',
    };

    const policy = experienceDetails?.policy
        ? {
              policyId: experienceDetails?.policy?.policyId,
              checked: false,
              checkedAt: undefined,
              name: experienceDetails?.policy?.name,
              description: experienceDetails?.policy?.description,
              requiresConsent: experienceDetails?.policy?.requiresConsent,
          }
        : undefined;

    switch (experienceDetails?.type) {
        case 'commercial':
            bookingInfo = {
                ...bookingInfo,
                reqMeetingRoom: false,
                requiresCatering: false,
                cateringNotes: '',
                guests: 0,
            };
            break;
        case 'vehicle':
            bookingInfo = {
                ...bookingInfo,
                driverSameAsBooker: false,
                drivers: [],
            };
            break;
        case 'boat':
            bookingInfo = {
                ...bookingInfo,
                adults: 0,
                children: 0,
                overnightGuests: 0,
                requiresCatering: false,
                cateringNotes: '',
                requiresBeverages: false,
                beverageNotes: '',
            };
            break;
        case 'activity':
            bookingInfo = {
                ...bookingInfo,
                guests: 0,
            };
            break;
        default:
            bookingInfo = {
                ...bookingInfo,
                adults: 0,
                children: 0,
                ...(overnight ? { overnightGuests: 0 } : {}),
                requiresCatering: false,
                cateringNotes: '',
                requiresBeverages: false,
                beverageNotes: '',
                requiresMassages: false,
                massageNotes: '',
            };
            break;
    }

    if (user.role.includes('owner')) {
        values = {
            booker: { id: user.sub },
            onBehalf: false,
            reason: 'other',
            notes: '',
            policy,
            bookingInfo,
            spaces: [],
            removeNirovisionAccess: false,
            requiresServices: false,
        };
    }

    if (user.role.includes('guest')) {
        values = {
            booker: { id: user.role === 'guest_user' ? user.sub : undefined },
            policy,
            bookingInfo: bookingInfo,
            spaces: [],
            jobs: {},
            requiresServices: false,
            rsvpConfig: {
                rsvpEnabled: false,
                rsvpMessage: DEFAULT_TEMPLATE,
                autoRemindersEnabled: true,
                reminderDaysBeforeEvent: 7,
            },
        };
    }

    return values;
};
